import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { GET_RACE_INSIGHTS } from '../../graphql/queries/raceInsights';
import { useBrand } from '../brand';
import { useTrack } from '../track';
import { useViewer } from '../shell';
import { Help, Seg } from '../primitives';
import { money, pct, roiDirection, roiText } from '../lib/format';
import { indexTone, lowSample, rate, MIN_SAMPLE, type Bucket, type Overlay } from '../track-stats/lib';
import './race-stats.css';

interface RaceInsights {
  raceId: string;
  trackCode: string;
  trackName: string;
  raceNumber: number;
  distance: number | null;
  fieldSize: number;
  trackRaces: number;
  byDistance: Bucket | null;
  byClass: Bucket | null;
  byRaceNumber: Bucket;
  byFieldSize: Bucket | null;
  barriers: { barrier: string; runners: number; wins: number; winIndex: number | null }[];
  settling: { style: string; winners: number; winnerPct: number }[];
}

const raceNumberLabel = (key: string) => (key === 'R10+' ? 'Races 10+' : `Race ${key.slice(1)}`);

/**
 * Race Statistics: how past races like this one at the track have gone (same distance,
 * class group, race number and field size). Mounted only when a member opens it, so
 * the query runs per race on demand.
 */
export function RaceStats({ raceId }: { raceId: string }) {
  const brand = useBrand();
  const track = useTrack();
  const { trackStats } = useViewer();
  const [overlay, setOverlay] = useState<Overlay>('tip');
  const { data, loading, error } = useQuery<{ raceInsights: RaceInsights | null }>(GET_RACE_INSIGHTS, { variables: { raceId } });
  const d = data?.raceInsights;

  if (error) return <p className="ui-rst-msg">Race statistics could not be loaded.</p>;
  if (!d) return <p className="ui-rst-msg">{loading ? 'Loading race statistics…' : 'No statistics for this race yet.'}</p>;

  const tipsName = overlay === 'tip' ? 'Our top pick' : `${brand.markName}s`;
  const at = d.distance ? `${d.distance}m` : null;
  const cards: { label: string; value: string; b: Bucket }[] = [
    ...(d.byDistance && at ? [{ label: 'Distance', value: at, b: d.byDistance }] : []),
    ...(d.byClass ? [{ label: 'Class', value: d.byClass.key, b: d.byClass }] : []),
    { label: 'Race number', value: raceNumberLabel(d.byRaceNumber.key), b: d.byRaceNumber },
    ...(d.byFieldSize ? [{ label: 'Field size', value: d.byFieldSize.key, b: d.byFieldSize }] : []),
  ];

  return (
    <div className="ui-rst">
      <div className="ui-rst-head">
        <p>
          Past races like this at {d.trackName} over the last two years.
          <Help
            title="Race stats"
            text={`Each card is the races here that share one thing with this race. ROI is a level $1 win bet at SP. Greyed figures come from fewer than ${MIN_SAMPLE} races.`}
          />
        </p>
      </div>

      {at && d.barriers.length > 0 && (
        <section className="ui-rst-sec" aria-label={`Barriers at ${at}`}>
          <h4>
            Barriers at {at}
            <Help title="Barriers" text="How often each barrier wins against its fair share of the field. 1.0 is fair; 1.3 wins 30% more often than a random draw." />
          </h4>
          <ol className="ui-rst-bars">
            {d.barriers.map((c) => (
              <li key={c.barrier} data-tone={indexTone(c)} title={`${c.wins} wins from ${c.runners} runners`}>
                <span>{c.barrier}</span>
                <b>{c.winIndex != null ? c.winIndex.toFixed(2) : '–'}</b>
              </li>
            ))}
          </ol>
        </section>
      )}

      {at && d.settling.length > 0 && (
        <section className="ui-rst-sec" aria-label={`Where winners settled at ${at}`}>
          <h4>
            Where winners settled at {at}
            <Help title="Settling" text="Winners' settling position in the run: leader, on pace (2nd–3rd), midfield (front 60%) or back." />
          </h4>
          <ul className="ui-rst-settle">
            {d.settling.map((s) => (
              <li key={s.style}>
                <span>{s.style}</span>
                <i style={{ ['--w' as string]: `${s.winnerPct}%` }} />
                <b>{pct(s.winnerPct, 0)}</b>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="ui-rst-tgl">
        <Seg
          label="Tips shown"
          value={overlay}
          onChange={(v) => {
            setOverlay(v);
            track('ui_toggle', { control: 'race_stats_overlay', value: v, trackName: d.trackCode, raceNumber: d.raceNumber });
          }}
          options={[
            { value: 'tip', label: 'Our top pick' },
            { value: 'consensus', label: `${brand.markName}s` },
          ]}
        />
      </div>

      <div className="ui-rst-cards">
        {cards.map((c) => (
          <StatCard key={c.label} label={c.label} value={c.value} b={c.b} overlay={overlay} tipsName={tipsName} />
        ))}
      </div>

      {trackStats && (
        <Link className="ui-rst-more" to={`/tracks/${d.trackCode}`} onClick={() => track('ui_click', { target: 'race_stats_track_stats', trackName: d.trackCode })}>
          All {d.trackName} statistics
        </Link>
      )}
    </div>
  );
}

function StatCard({ label, value, b, overlay, tipsName }: { label: string; value: string; b: Bucket; overlay: Overlay; tipsName: string }) {
  const tips = b[overlay];
  const winners = b.winnersShort + b.winnersMid + b.winnersLong;
  const share = (n: number) => rate(n, winners) ?? 0;
  const low = lowSample(b.races);
  return (
    <div className="ui-rst-card" data-low={low}>
      <div className="ch">
        <span className="k">{label}</span>
        <b>{value}</b>
        <span className="n">
          {b.races} {b.races === 1 ? 'race' : 'races'}
          {low && b.races > 0 && ' · small sample'}
        </span>
      </div>
      {b.races === 0 ? (
        <p className="ui-rst-msg">No past races here.</p>
      ) : (
        <>
          <div className="kv">
            <div>
              <span>Favourite won</span>
              <b>{pct(rate(b.favWins, b.races), 0)}</b>
            </div>
            <div data-low={lowSample(tips.races)}>
              <span>{tipsName} won</span>
              <b>{pct(rate(tips.wins, tips.races), 0)}</b>
              <small>
                ROI <em className={roiDirection(tips.roi)}>{roiText(tips.roi)}</em>
              </small>
            </div>
            <div>
              <span>Median winner</span>
              <b>{money(b.medianWinnerSp)}</b>
            </div>
          </div>
          {winners > 0 && (
            <div className="px" aria-label={`Winners: ${pct(share(b.winnersShort), 0)} under $4, ${pct(share(b.winnersMid), 0)} $4 to $10, ${pct(share(b.winnersLong), 0)} over $10`}>
              <div className="bar" aria-hidden="true">
                <i className="s" style={{ width: `${share(b.winnersShort)}%` }} />
                <i className="m" style={{ width: `${share(b.winnersMid)}%` }} />
                <i className="l" style={{ width: `${share(b.winnersLong)}%` }} />
              </div>
              <div className="lg" aria-hidden="true">
                <span className="s">Under $4 {pct(share(b.winnersShort), 0)}</span>
                <span className="m">$4–$10 {pct(share(b.winnersMid), 0)}</span>
                <span className="l">Over $10 {pct(share(b.winnersLong), 0)}</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
